import { getUnitOfMeasureCookie } from '@app/components/common/UnitOfMeasureToggle/utils';
import {
  useEspot,
  usePriceAndAvailability,
  useSystemFeedback,
  useFitmentCheck,
  useGridListData,
  useAnalytics
} from '@app/hooks';
import { AnchorMenu, useModal } from 'cat-ecommerce-alloy';
import debounce from 'lodash/debounce';
import {
  getFormattedUtcDatestamp,
  getSessionStorage,
  removeSessionStorage,
  replaceTokensInString
} from '@app/utils';
import { getProductDetails } from '@app/store/products/actions';
import { getModernizedEquipmentFitment } from '@app/store/myequipment/actions';
import * as types from '@app/store/myequipment/constants';
import { normalizeError } from '@app/store/exception/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  STATUS,
  UOM,
  USER_TYPE_GUEST,
  BUNDLE_BEAN,
  PACKAGE_BEAN,
  ESPOT_VARIANTS,
  USER_TYPE_REGISTERED,
  TOAST_TYPE,
  PART_QTY
} from '@app/constants/commonConstants';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  FEEDBACK_TYPES
} from '@app/constants/errorConstants';
import { XSELL } from '@app/constants/productAssociation';
import { loadSavedListDetails } from '@app/store/account/actions';
import BreadCrumb from '@app/components/common/BreadCrumb';
import Conditional from '@app/components/common/Conditional';
import Espot from '@app/components/common/Espot/Espot';
import RenderException from '@app/components/common/Exception/RenderException';
import useSavedLists from '@app/components/saved-lists/useSavedLists';
import {
  COMPATIBILITY_ID,
  DESCRIPTION_ID,
  DIAGRAMS_ID,
  RELATED_PARTS_ID,
  SPECIFICIFICATIONS_ID,
  ESPOTS,
  LUCID_BREADCRUMB_KEY,
  SOLR_BREADCRUMB_KEY
} from '../constants';
import { PCC_PDP_LUCID_FLAG } from '@app/constants/featureFlags';
import ProductAnalytics from '../ProductAnalytics';
import ProductComparisonTable from '../ProductComparisonTable';
import ProductCompatibility from '../ProductCompatibility';
import ProductDescription from '../ProductDescription';
import ProductDiagrams from '../ProductDiagrams';
import ProductLegalNotices from '../ProductLegalNotices';
import ProductOverview from '../ProductOverview';
import ProductRelatives from '../ProductRelatives';
import ProductSpecifications from '../ProductSpecifications';
import { pushFusionQueryId, updateBrowsingHistoryCookie } from './utils';
import AddSavedListModal from '@app/components/saved-lists/AddSavedListModal';
import CreateSavedListModal from '@app/components/saved-lists/CreateSavedListModal';
import { SAVE_TYPE } from '../../checkout/shopping-cart/OrderItems/constants';
import { getLanguageDetails } from '../../account/shopping-preferences/utils';
import { loadPreferences } from '@app/store/shoppingPreferences/actions';
import queryString from 'query-string';
import ProductSkeletonLoader from '../../product/ProductSkeletonLoader';
import {
  LUCID_PART_NUMBER_KEY,
  SOLR_PART_NUMBER_KEY
} from '@app/constants/part';
import KitPartsCards from '../KitPartsCards/KitPartsCards';
import {
  PCC_PDP_BUNDLE_KITS_LIST,
  PCC_PDP_KITS_LIST
} from '@app/constants/analyticsConstants';
import { getPartName } from '@app/utils/commonUtils';
import { ERROR_INFO } from './constant';
import styles from '../styles.module.scss';
import cx from 'classnames';
import WrapperContainer from '@app/components/common/WrapperContainer';
import ToastMessages from '@app/components/common/Blocks/ToastMessages';

const RELATED_PARTS_GA_LIST_NAME = 'PCC PDP Page Related Parts';

const ProductDisplayPage = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { setError } = useSystemFeedback();
  const { fireProductImpressionEvent } = useAnalytics();
  const { actualURL } = useLocation();
  const addSavedListModalProps = useModal();
  const createSavedListModalProps = useModal();
  const {
    data: priceAndAvailabilityData,
    error: priceCallError,
    invoke: getPriceAndAvailability,
    status: priceCallStatus
  } = usePriceAndAvailability({ useCacheService: true });
  const [showToaster, setShowToaster] = useState(false);

  const initialPriceCallMade = useRef(false);
  const [quantity, setQuantity] = useState(1);
  const [unitOfMeasure, setUnitOfMeasure] = useState(
    getUnitOfMeasureCookie('uom') ?? UOM.us
  );
  const isFirstCall = useRef(true);

  const {
    saveListsResolved,
    isPendingSavedListsModal,
    setIsPendingSavedListsModal
  } = useSavedLists();
  const userType = useSelector(s => s.common?.userType);
  const isGuest = userType === USER_TYPE_GUEST;
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const locale = useSelector(s => s.common?.locale);
  const getCommonVarsStatus = useSelector(s => s.common?.getCommonVarsStatus);
  const dealerName = useSelector(s => s.dealer?.dealerName);
  const shouldCallProductDetail = getCommonVarsStatus === STATUS.RESOLVED;
  const current = useSelector(s => s.products?.current);
  const getProductDetailsStatus = useSelector(
    s => s.products.getProductDetailsStatus
  );
  const evaluationStatusList = [STATUS.REJECTED, STATUS.RESOLVED];
  const isDetailStatusCompleted = evaluationStatusList.includes(
    getProductDetailsStatus
  );

  const productInfoStatus = useSelector(
    s => s.products?.getProductDetailsStatus
  );
  const productInfoError = useSelector(s => s.products?.getProductDetailsError);
  const isModernizedPDP = useSelector(s => s.featureFlag[PCC_PDP_LUCID_FLAG]);
  let breadCrumbKey = LUCID_BREADCRUMB_KEY;
  let partNumberKey = LUCID_PART_NUMBER_KEY;

  if (!isModernizedPDP) {
    breadCrumbKey = SOLR_BREADCRUMB_KEY;
    partNumberKey = SOLR_PART_NUMBER_KEY;
  }

  const breadcrumbLinks = useSelector(s => s.products.byId?.[current])?.[
    breadCrumbKey
  ];
  const fitmentEnabled = useSelector(
    s => s.products.byId?.[current]?.isFilterByEquipmentEnabled
  );
  const packageQty = priceAndAvailabilityData[current]?.packageQty;
  const partNumber = useSelector(s => s.products.byId?.[current]?.partNumber);
  const uniqueId = useSelector(s => s.products.byId?.[current]?.uniqueId);
  const savedListsStatus = useSelector(s => s.account?.savedListsStatus);
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const merchandisingAssociations = useSelector(
    s => s.products.byId?.[current]?.merchandisingAssociations
  );

  const beanType = useSelector(s => s.products.byId?.[current]?.type);
  const productComponents = useSelector(
    s => s.products.byId?.[current]?.components
  );
  const savedLists = useSelector(s => s.account?.savedLists);
  const hasSavedLists = savedLists?.length > 0;

  const equipmentFitmentStatus = useSelector(
    s => s.myEquipment?.equipmentFitmentStatus
  );

  const fitmentError = useSelector(s => s.errors?.pdp);

  const isPackage = beanType === PACKAGE_BEAN;
  const isBundle = beanType === BUNDLE_BEAN;
  const productListName = isPackage
    ? PCC_PDP_KITS_LIST
    : PCC_PDP_BUNDLE_KITS_LIST;
  const isPackageOrBundle = isPackage || isBundle;
  const ispackageOrBundlesWithComponents =
    isPackageOrBundle && productComponents?.length;
  const isPackageWithItemAssociated =
    (productComponents && productComponents.length > 0) ?? false;

  const getPriceAndAvailabilityByCurrentProduct = useCallback(
    ({ quantity, parts }) => {
      if (parseInt(quantity) > 0) {
        const currentPartDetails = {
          partNumber: partNumber,
          quantity,
          uniqueId
        };
        const maPartDetails = (merchandisingAssociations ?? []).map(ma => ({
          partNumber: ma[partNumberKey],
          quantity: 1,
          uniqueId: ma.uniqueId
        }));
        getPriceAndAvailability({
          parts:
            parts && parts.length
              ? parts
              : [currentPartDetails, ...maPartDetails]
        });
      }
    },

    [getPriceAndAvailability, merchandisingAssociations, partNumber, uniqueId]
  );
  const uom = useSelector(
    s => s.shoppingPreferences?.shoppingPreferencesData?.uom
  );

  const getLanguageStatus = useSelector(s => s.footer?.getLanguageStatus);
  const supportedLanguages = useSelector(s => s.footer?.supportedLanguages);
  const languageOptions = useMemo(
    () =>
      getLanguageStatus === STATUS.RESOLVED
        ? getLanguageDetails(supportedLanguages)
        : [],
    [getLanguageStatus, supportedLanguages]
  );

  const { getFitmentCheck } = useFitmentCheck();
  const { getFitmentCheckResult } = useGridListData();

  useEffect(() => {
    if (isDetailStatusCompleted && ispackageOrBundlesWithComponents) {
      if (isFirstCall.current === true) {
        const updatedProductComponents = productComponents?.map(
          ({ name, partNumber, ...element }) => {
            return {
              ...element,
              name: getPartName(partNumber, name),
              partNumber
            };
          }
        );
        fireProductImpressionEvent(productListName, updatedProductComponents);
        isFirstCall.current = false;
      }
    }
  }, [
    productComponents,
    fireProductImpressionEvent,
    isDetailStatusCompleted,
    ispackageOrBundlesWithComponents,
    productListName
  ]);

  useEffect(() => {
    const { scrollTo } = queryString.parse(window.location?.search);
    getLanguageStatus === STATUS.RESOLVED &&
      userType === USER_TYPE_REGISTERED &&
      dispatch(loadPreferences({ scrollTo, languages: languageOptions }));
  }, [dispatch, getLanguageStatus, languageOptions]);

  const espotNames = ESPOTS[beanType]?.espotNames;
  const topEspot = ESPOTS[beanType]?.espot1;
  const bottomEspot = ESPOTS[beanType]?.espot7;
  const paramTargeting = actualURL?.search
    ? `&${actualURL?.search.split('?')[1]}`
    : '';
  const pdpEspotParams = `&productId=${current}${paramTargeting}`;
  const { getEspotContent, isEspotLoading } = useEspot(
    espotNames,
    pdpEspotParams
  );

  const merchandisingAssociationsPartNumbers = merchandisingAssociations?.map(
    part => part[partNumberKey]
  );

  const isCurrentLoaded = current && productInfoStatus === STATUS.RESOLVED;
  const didPriceCallError = !!priceCallError;
  const didFitmentCallError = equipmentFitmentStatus === STATUS.REJECTED;
  const skipNextPriceCall = useRef(false);
  const lastSerialCheckedForFitment = useRef();
  const partNumber_QTY = getSessionStorage(PART_QTY);
  const qty = partNumber_QTY?.split('_')[1];

  // Disabled exhaustive-deps here because React doesn't know about debounce
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPriceAndAvailabilityCall = useCallback(
    debounce(
      newQuantity =>
        getPriceAndAvailabilityByCurrentProduct({
          quantity: newQuantity
        }),
      1000
    ),
    [partNumber]
  );

  const navs = useMemo(
    () => [
      ...(fitmentEnabled && !isPackageOrBundle
        ? [{ id: DIAGRAMS_ID, title: t('PDP_SCHEMATIC_DIAGRAM') }]
        : []),
      { id: DESCRIPTION_ID, title: t('DESCRIPTION') },
      { id: SPECIFICIFICATIONS_ID, title: t('SPECIFICATIONS') },
      ...(fitmentEnabled
        ? [{ id: COMPATIBILITY_ID, title: t('COMPATIBLE_MODELS') }]
        : [])
    ],
    [fitmentEnabled, isPackageOrBundle, t]
  );

  // Fetches product information
  const productDetailActionsHasBeenDispatched = useRef(false);
  useEffect(() => {
    if (
      current &&
      shouldCallProductDetail &&
      !productDetailActionsHasBeenDispatched.current
    ) {
      productDetailActionsHasBeenDispatched.current = true;
      dispatch(getProductDetails());
    }
  }, [current, shouldCallProductDetail, dispatch]);

  const registrationPendingApproval = useSelector(
    s => s.common?.registrationPendingApproval
  );
  // Fetches initial price & availability information
  useEffect(() => {
    if (
      !isCatCorp &&
      isCurrentLoaded &&
      !initialPriceCallMade?.current &&
      registrationPendingApproval !== undefined &&
      !registrationPendingApproval
    ) {
      // This should be fired either once or twice (depending on if the packageQty is greater than 1)
      let parts = [];
      if (isBundle && isPackageWithItemAssociated) {
        const bundleItems = productComponents.map(items => ({
          partNumber: items.partNumber,
          quantity: items.quantity * quantity,
          uniqueId: items.uniqueId
        }));
        const currentBundle = {
          partNumber,
          quantity,
          uniqueId
        };
        parts = [currentBundle, ...bundleItems];
      }

      getPriceAndAvailabilityByCurrentProduct({
        quantity: qty ?? 1,
        parts
      });
    }
  }, [
    getPriceAndAvailabilityByCurrentProduct,
    isCatCorp,
    isCurrentLoaded,
    registrationPendingApproval
  ]);

  // Re-fetches price & availability info when package quantity is specified
  useEffect(() => {
    if (packageQty > 1 && !isCatCorp && isCurrentLoaded) {
      /**
       * This condition should always fire exactly one time (when the packageQty is initially set and it is bigger than 1. In case packageQty is 1 we don't need to call again priceAndAvailability ).
       * The quantity needs to be updated to the packageQty thus the availability must be called again
       * to confirm that the packageQty is available. This is a band-aid related to the Price and Avail call
       * not being able to retrieve packageQty without triggering an error in the dealer service.
       */
      getPriceAndAvailabilityByCurrentProduct({ quantity: packageQty });
      // Set skipNextPriceCall ref to true so that the debounced version of price&availability doesn't fire when we manually set quantity here
      skipNextPriceCall.current = true;
      setQuantity(packageQty);
    }
    if (
      !!partNumber_QTY &&
      partNumber_QTY.split('_').includes(partNumber.toLowerCase())
    ) {
      skipNextPriceCall.current = true;
      setQuantity(qty);
    }
  }, [
    getPriceAndAvailabilityByCurrentProduct,
    isCatCorp,
    isCurrentLoaded,
    packageQty
  ]);

  // Sets whether priceAndAvailability has been resolved at least one
  // not to show a loader in price for subsequent calls
  useEffect(() => {
    if (priceCallStatus === STATUS.RESOLVED) {
      initialPriceCallMade.current = true;
      if (!!partNumber_QTY) {
        setShowToaster(true);
      }
      removeSessionStorage(PART_QTY);
    }
  }, [priceCallStatus]);

  // Re-fetches price & availability info when the quantity changes
  // TODO: do not call price and availability on error
  useEffect(() => {
    const shouldMakePriceCall =
      initialPriceCallMade?.current && !skipNextPriceCall.current && quantity;

    if (shouldMakePriceCall && !isBundle) {
      debouncedPriceAndAvailabilityCall(quantity);
    }
    skipNextPriceCall.current = false;
  }, [debouncedPriceAndAvailabilityCall, quantity, isBundle]);

  // Fetches fitment information
  const isFitmentLoading = equipmentFitmentStatus === STATUS.PENDING;
  const equipmentFitmentStatusResolve = evaluationStatusList.includes(
    equipmentFitmentStatus
  );

  useEffect(() => {
    if (
      isCurrentLoaded &&
      !!serialNumber &&
      (lastSerialCheckedForFitment.current !== serialNumber ||
        equipmentFitmentStatus === STATUS.IDLE)
    ) {
      lastSerialCheckedForFitment.current = serialNumber;
      dispatch(
        getModernizedEquipmentFitment({
          partNumbers: merchandisingAssociationsPartNumbers
            ? [partNumber, ...merchandisingAssociationsPartNumbers]
            : partNumber,
          type: types.PDP_GET_FITMENT_SET_STATUS,
          errorInfo: ERROR_INFO
        })
      );
    }
  }, [
    dispatch,
    equipmentFitmentStatus,
    isCurrentLoaded,
    partNumber,
    merchandisingAssociationsPartNumbers,
    serialNumber
  ]);
  // Fetches saved lists information
  useEffect(() => {
    if (savedListsStatus === STATUS.IDLE && isCurrentLoaded && !isGuest) {
      dispatch(loadSavedListDetails());
    }
  }, [dispatch, isCurrentLoaded, isGuest, savedListsStatus]);

  // Adds part to browsing history cookie
  useEffect(() => {
    if (partNumber) {
      updateBrowsingHistoryCookie(partNumber);
      pushFusionQueryId();
    }
  }, [partNumber]);
  // Handle saved list modal after login
  useEffect(() => {
    if (saveListsResolved && isPendingSavedListsModal && !isGuest) {
      setIsPendingSavedListsModal(false);
      if (hasSavedLists) {
        addSavedListModalProps.open();
      } else {
        createSavedListModalProps.open();
      }
    }
  }, [isGuest, isPendingSavedListsModal, saveListsResolved]);

  // Handle price & availability call and fitment service errors
  useEffect(() => {
    if (didPriceCallError) {
      const priceCallError = {
        message: `${t('PA_TIMEOUT')} ${getFormattedUtcDatestamp(locale)}`,
        severity: FEEDBACK_TYPES.ERROR,
        title: 'GENERIC_ERROR'
      };
      setError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN, priceCallError);
    } else if (didFitmentCallError) {
      setError(
        ERROR_DOMAIN.PDP,
        ERROR_PATH.MAIN,
        normalizeError(fitmentError?.message)
      );
    }
  }, [didFitmentCallError, didPriceCallError, locale, setError, t]);

  const data = {
    items: [
      {
        partNumber,
        quantity: quantity
      }
    ],
    saveType: SAVE_TYPE.DEFAULT
  };
  // Shows error if product information services fails
  if (productInfoStatus === STATUS.REJECTED) {
    throw new RenderException(normalizeError(productInfoError));
  }

  const isCatCSR = useSelector(s => s.common?.isCatCSR) || false;
  const isBuyOnBehalf = useSelector(s => s.common?.isBuyOnBehalf) || false;

  const anchorMenuProps = useMemo(() => {
    return {
      className: 'd-none d-lg-block',
      navs,
      scrollPaddingFactor: 150,
      fixedTopOffset: 120,
      classWhenFixed: cx(styles['anchor-menu-bar'], {
        [`${styles['is-csr']}`]: isCatCSR || isBuyOnBehalf
      })
    };
  }, [navs]);

  return (
    <Conditional fallback={<ProductSkeletonLoader />} test={isCurrentLoaded}>
      {showToaster && (
        <WrapperContainer>
          <ToastMessages
            type={TOAST_TYPE.SUCCESS}
            message={replaceTokensInString(
              t('AVAILABLE_STORE_CHANGE_CONFIRMATION'),
              dealerName
            )}
            autoClose={false}
          />
        </WrapperContainer>
      )}
      <div className="legacy-wrapper container">
        <ProductAnalytics
          current={current}
          fitmentCheckStatus={equipmentFitmentStatus}
          isCatCorp={isCatCorp}
          isCurrentLoaded={isCurrentLoaded}
          priceCallStatus={priceCallStatus}
          serialNumber={serialNumber}
          getFitmentCheck={getFitmentCheck}
        />
        {breadcrumbLinks && (
          <BreadCrumb links={breadcrumbLinks} backButtonText={t('UC_BACK')} />
        )}

        <Espot
          html={getEspotContent(topEspot)}
          variant={ESPOT_VARIANTS.CAROUSEL}
          isLoading={isEspotLoading}
        />
        <ProductOverview
          espotHtml={getEspotContent(ESPOTS[beanType]?.espot2 || '')}
          fitmentEnabled={fitmentEnabled}
          initialPriceCallMade={initialPriceCallMade?.current}
          priceCallStatus={priceCallStatus}
          quantity={quantity}
          setQuantity={setQuantity}
          didFitmentCallResolve={equipmentFitmentStatusResolve}
          getFitmentResult={getFitmentCheckResult}
          getFitmentCheck={getFitmentCheck}
          isPackage={isPackage}
          isPackageWithItemAssociated={isPackageWithItemAssociated}
          selectedQuantity={quantity}
        />
        {isPackageOrBundle && (
          <KitPartsCards
            isPackage={isPackage}
            initialPriceCallMade={initialPriceCallMade?.current}
            items={productComponents}
            quantity={quantity}
            priceCallStatus={priceCallStatus}
            isPackageWithItemAssociated={isPackageWithItemAssociated}
          />
        )}
        <div className="mt-5">
          <AnchorMenu {...anchorMenuProps} />

          {!isPackageOrBundle && (
            <Espot
              html={getEspotContent(ESPOTS[beanType]?.espot3)}
              variant={ESPOT_VARIANTS.LARGE}
              isLoading={isEspotLoading}
            />
          )}
          {!isPackageOrBundle && fitmentEnabled && <ProductDiagrams />}
          <ProductDescription productId={current} />

          {!isPackageOrBundle && (
            <Espot
              html={getEspotContent(ESPOTS[beanType]?.espot4)}
              variant={ESPOT_VARIANTS.LARGE}
              isLoading={isEspotLoading}
            />
          )}
          <ProductSpecifications
            productId={current}
            setUnitOfMeasure={setUnitOfMeasure}
            unitOfMeasure={uom ?? unitOfMeasure}
          />
          {!isPackageOrBundle && (
            <Espot
              html={getEspotContent(ESPOTS[beanType]?.espot5)}
              variant={ESPOT_VARIANTS.LARGE}
              isLoading={isEspotLoading}
            />
          )}
          {!isPackage && (
            <ProductComparisonTable
              productId={current}
              unitOfMeasure={unitOfMeasure}
              didFitmentCallResolve={equipmentFitmentStatusResolve}
              getFitmentResult={getFitmentCheckResult}
              getFitmentCheck={getFitmentCheck}
              priceCallStatus={priceCallStatus}
            />
          )}
          {!isPackage && (
            <ProductRelatives
              filter={p => p.associationType === XSELL}
              id={RELATED_PARTS_ID}
              productId={current}
              title={t('RELATED_PARTS')}
              gaListName={RELATED_PARTS_GA_LIST_NAME}
              isFitmentLoading={isFitmentLoading}
              getFitmentCheck={getFitmentCheck}
              didFitmentCallResolve={equipmentFitmentStatusResolve}
              getFitmentResult={getFitmentCheckResult}
              priceCallStatus={priceCallStatus}
            />
          )}
          {!isPackageOrBundle && (
            <Espot
              html={getEspotContent(ESPOTS[beanType]?.espot6)}
              variant={ESPOT_VARIANTS.LARGE}
              isLoading={isEspotLoading}
            />
          )}
          {fitmentEnabled && <ProductCompatibility productId={current} />}
          <Espot
            html={getEspotContent(bottomEspot)}
            variant={ESPOT_VARIANTS.LARGE}
            isLoading={isEspotLoading}
          />
          <ProductLegalNotices />
        </div>
      </div>

      <AddSavedListModal {...addSavedListModalProps} addSavedListData={data} />
      <CreateSavedListModal
        {...createSavedListModalProps}
        createSavedListData={data}
      />
    </Conditional>
  );
};

export default ProductDisplayPage;
